import React from "react";
import { X } from "lucide-react";

const WorkspaceDialog = ({
  dialogConfig,
  onClose,
  onCancel,
  onConfirm,
  onInputChange,
  activeWorkspaceEnv,
  theme,
}) => {
  if (!dialogConfig?.isOpen) return null;

  const isJuniorEnv =
    activeWorkspaceEnv === "scratch_jr" ||
    activeWorkspaceEnv === "junior" ||
    activeWorkspaceEnv === "junior_blocks_basic" ||
    activeWorkspaceEnv === "junior_blocks_adv";

  const isJr = theme === "jr" || (theme !== "dark" && isJuniorEnv);
  const isScratchTheme =
    theme === "light" || isJr || activeWorkspaceEnv === "scratch";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-xs p-4 animate-in fade-in duration-200">
      <div
        className={`
          w-full max-w-sm flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200
          ${
            isJr
              ? "rounded-3xl bg-[#FAF6EE] border-4 border-[#E6C896] shadow-2xl text-[#3D2610]"
              : isScratchTheme
              ? "rounded-2xl bg-white ring-1 ring-neutral-200 shadow-xl text-neutral-900 border-0"
              : "rounded-2xl bg-[#13151b] ring-1 ring-[#27272a] shadow-[0_20px_50px_rgba(0,0,0,0.8)] text-white border-0"
          }
        `}
      >
        <div
          className={`p-4 flex flex-col gap-2 ${
            isJr
              ? "bg-gradient-to-b from-[#FFF3D1]/60 to-transparent"
              : ""
          }`}
        >
          <div className="flex items-start justify-between mb-1">
            <h3
              className={isJr ? "font-black text-base" : "font-bold text-base"}
              style={{
                color: isJr
                  ? "#3D2610"
                  : isScratchTheme
                  ? "#111827"
                  : "#ffffff",
              }}
            >
              {dialogConfig.title}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors border-0 cursor-pointer ${
                isJr
                  ? "bg-[#FEE2E2] text-[#DC2626] hover:bg-[#FECACA]"
                  : isScratchTheme
                  ? "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                  : "text-neutral-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {dialogConfig.message && (
            <p
              className={`text-xs leading-relaxed m-0 ${
                isJr ? "font-semibold" : ""
              }`}
              style={{
                color: isJr
                  ? "#78350F"
                  : isScratchTheme
                  ? "#374151"
                  : "#e2e8f0",
              }}
            >
              {dialogConfig.message}
            </p>
          )}

          {dialogConfig.showInput && (
            <div className="mt-2">
              <input
                type="text"
                value={dialogConfig.inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                autoFocus
                className={`
                  w-full px-3.5 py-2.5 text-xs focus:outline-none transition-colors
                  ${
                    isJr
                      ? "rounded-2xl bg-white border-2 border-[#E2C285] font-extrabold text-[#3D2610] focus:border-[#F59E0B] shadow-inner"
                      : isScratchTheme
                      ? "rounded-xl bg-neutral-50 ring-1 ring-neutral-200 font-medium text-neutral-900 focus:ring-2 focus:ring-purple-500 border-0"
                      : "rounded-xl bg-[#0a0b0f] ring-1 ring-[#27272a] font-medium text-white focus:ring-2 focus:ring-purple-500/50 border-0"
                  }
                `}
                placeholder="Enter project name..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") onConfirm();
                  if (e.key === "Escape") onClose();
                }}
              />
            </div>
          )}
        </div>

        <div
          className={`px-4 py-3 flex items-center justify-end gap-2.5 ${
            isJr
              ? "bg-[#FFF3D1] border-t-2 border-[#E6C896]"
              : isScratchTheme
              ? "bg-neutral-50 border-t border-neutral-100 border-0"
              : "bg-[#0c0d12] border-t border-[#222226] border-0"
          }`}
        >
          {dialogConfig.cancelText && (
          <button
            type="button"
            onClick={onCancel}
            className={
              isJr
                ? "px-4 py-2 rounded-2xl text-xs font-black bg-white hover:bg-[#FFFDF7] text-[#78350F] border-2 border-[#E2C285] shadow-[0_2px_0_#D4B06A] transition-all cursor-pointer"
                : "px-3.5 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer border-0"
            }
            style={
              isJr
                ? undefined
                : {
                    color: isScratchTheme ? "#4b5563" : "#cbd5e1",
                  }
            }
          >
            {dialogConfig.cancelText}
          </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            className={
              isJr
                ? "px-4 py-2 rounded-2xl text-xs font-black bg-gradient-to-b from-[#34D399] to-[#10B981] hover:from-[#10B981] hover:to-[#059669] text-white border-2 border-[#059669] shadow-[0_2px_0_#047857] transition-all cursor-pointer"
                : `px-4 py-2 rounded-xl text-xs font-bold shadow-md shadow-purple-600/30 transition-colors cursor-pointer border-0 ${dialogConfig.confirmClass}`
            }
          >
            {dialogConfig.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceDialog;
